import React from "react";

import { BrainCircuit, Download, ShieldAlert } from "lucide-react";
import { ResponsiveContainer, RadialBarChart, RadialBar, PolarAngleAxis } from "recharts";

import { ButtonShadcn } from "@/components/ui/Button";
import Skeleton from "@/components/shared/Skeleton";
import { Section, BarRow, BarsSkeleton, EmptyNote, KpiTile, Collapsible, LowSampleBadge, CHART, goodRateColor, fmtPct, ExportButtons } from "./primitives";

// F4 — AI extraction accuracy. Data's job: one headline "can I trust it" number
// (→ a radial gauge, which reads as a trust dial far better than a bar), a few
// secondary rates (→ tiles), and two ranked "where is it worst" lists + a
// calibration read (→ horizontal bars, worst-first). The radial gauge is unique
// to this section, so no form repeats across families.

const AccuracyGauge = ({ value, loading }) => {
  const pct = value ?? 0;
  const color = goodRateColor(pct);
  const chartData = [{ name: "accuracy", value: pct, fill: color }];
  if (loading) {
    return (
      <div className="flex items-center justify-center w-[150px] h-[150px]">
        <Skeleton className="h-32 w-32 rounded-full" />
      </div>
    );
  }
  return (
    <div className="relative w-[150px] h-[150px] flex-shrink-0">
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart innerRadius="72%" outerRadius="100%" data={chartData} startAngle={90} endAngle={-270}>
          <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
          <RadialBar background dataKey="value" cornerRadius={8} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className="text-[1.7rem] font-bold leading-none" style={{ color }}>
          {value === null || value === undefined ? "—" : `${pct}%`}
        </span>
        <span className="text-[10px] text-text-secondary mt-0.5">accuracy</span>
      </div>
    </div>
  );
};

const CalibrationLabel = { high: "High confidence", medium: "Medium", low: "Low confidence" };
const CalibrationColor = { high: CHART.green, medium: CHART.amber, low: CHART.red };

const RowSample = ({ row, minSampleSize }) =>
  row.belowMinSample ? (
    <LowSampleBadge minSampleSize={minSampleSize} />
  ) : (
    <span className="text-[9px] text-text-secondary tabular-nums shrink-0">n={row.confirmedFields}</span>
  );

const AiAccuracySection = ({ data, loading, onExportCsv, onExportPdf, exporting }) => {
  const byDoc = (data?.byDocumentType ?? []).slice(0, 8);
  const byField = (data?.byField ?? []).slice(0, 8);
  const calibration = data?.confidenceCalibration ?? [];
  const minSampleSize = data?.minSampleSize ?? 10;
  const noData = !loading && (data?.confirmedFields ?? 0) === 0;

  return (
    <Section
      icon={BrainCircuit}
      title="AI extraction accuracy"
      subtitle="When the AI reads a document, how accurate is it — and can we trust its confidence?"
      action={<ExportButtons onExportCsv={onExportCsv} onExportPdf={onExportPdf} loading={loading} busy={exporting} />}
    >
      {noData ? (
        <EmptyNote>No fields have been officer-confirmed in this period yet, so there is no ground truth to measure the AI against.</EmptyNote>
      ) : (
        <>
          {/* Confidence-inversion check — a distinct, always critical-severity
              signal from a merely low accuracy floor. Evaluated independently
              and shown up front, not buried in the calibration detail. */}
          {!loading && data?.confidenceInverted ? (
            <div className="rounded-xl p-3 mb-4 border border-tone-rose/30 bg-tone-rose/5 flex items-start gap-2">
              <ShieldAlert size={15} style={{ color: CHART.red }} className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[12px] font-semibold text-text-primary">Confidence inversion detected</p>
                <p className="text-[11px] text-text-secondary leading-snug">
                  A lower confidence band measures higher accuracy than a higher band (see calibration below). This is distinct from a low accuracy floor — it means the
                  AI&apos;s confidence score itself cannot be trusted to prioritize review effort right now.
                </p>
              </div>
            </div>
          ) : null}

          {/* Headline: gauge + secondary rate tiles */}
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-5 items-center">
            <div className="flex justify-center">
              <AccuracyGauge value={data?.overallAccuracy} loading={loading} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiTile label="Correction rate" value={fmtPct(data?.correctionRate)} color={CHART.amber} loading={loading} hint="officer had to fix it" />
              <KpiTile label="Blank-miss rate" value={fmtPct(data?.blankMissRate)} color={CHART.red} loading={loading} hint="AI missed the field" />
              <KpiTile label="Extraction reliability" value={fmtPct(data?.extractionReliability)} color={CHART.sky} loading={loading} hint="completed ÷ attempted" />
              <KpiTile
                label="Fields measured"
                value={loading ? undefined : (data?.confirmedFields ?? 0)}
                color={CHART.indigo}
                loading={loading}
                hint="confirmed ground-truth pairs"
              />
            </div>
          </div>

          {/* Detail breakdown — collapsed by default */}
          <Collapsible title="Accuracy by document type, by field & confidence calibration">
            {/* Two ranked worst-first lists */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div>
                <p className="text-[12px] font-medium text-text-primary mb-2">Accuracy by document type (worst first)</p>
                {loading ? (
                  <BarsSkeleton rows={5} />
                ) : byDoc.length === 0 ? (
                  <EmptyNote>No document-type data.</EmptyNote>
                ) : (
                  byDoc.map((d) => (
                    <BarRow
                      key={d.key}
                      label={d.label}
                      sublabel={`${d.correct}/${d.confirmedFields} correct`}
                      pct={d.accuracy}
                      valueLabel={fmtPct(d.accuracy)}
                      color={d.belowMinSample ? CHART.slate : goodRateColor(d.accuracy)}
                    />
                  ))
                )}
              </div>
              <div>
                <p className="text-[12px] font-medium text-text-primary mb-2">Accuracy by field (worst first)</p>
                {loading ? (
                  <BarsSkeleton rows={5} />
                ) : byField.length === 0 ? (
                  <EmptyNote>No field-level data.</EmptyNote>
                ) : (
                  byField.map((d) => (
                    <BarRow
                      key={d.key}
                      label={d.label}
                      sublabel={`${d.correct}/${d.confirmedFields} correct`}
                      pct={d.accuracy}
                      valueLabel={fmtPct(d.accuracy)}
                      color={d.belowMinSample ? CHART.slate : goodRateColor(d.accuracy)}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Confidence calibration */}
            <div className="mt-5">
              <p className="text-[12px] font-medium text-text-primary mb-2">Confidence calibration — is the AI right when it&apos;s confident?</p>
              {loading ? (
                <BarsSkeleton rows={3} />
              ) : calibration.length === 0 ? (
                <EmptyNote>No confidence data.</EmptyNote>
              ) : (
                calibration.map((c) => (
                  <BarRow
                    key={c.level}
                    label={CalibrationLabel[c.level] ?? c.level}
                    sublabel={`${c.correct}/${c.confirmedFields} correct`}
                    pct={c.accuracy}
                    valueLabel={fmtPct(c.accuracy)}
                    color={c.belowMinSample ? CHART.slate : (CalibrationColor[c.level] ?? CHART.slate)}
                  />
                ))
              )}
              <p className="text-[10px] text-text-secondary mt-1.5">
                Well-calibrated = High ≫ Low. A flat line means the confidence signal can&apos;t be trusted; an inverted line (see the banner above, when it fires) means
                it&apos;s actively misleading.
              </p>
            </div>
          </Collapsible>
        </>
      )}
    </Section>
  );
};

export default AiAccuracySection;
